import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './CustomDropdown.module.css';

const CustomDropdown = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  required = false,
  error,
  helperText,
  className = '',
  labelClassName = '',
  isMulti = false,
  isSearchable = true,
  isClearable = true,
  isLoading = false,
  loadingMessage = () => 'Loading...',
  noOptionsMessage = () => 'No options available',
  size = 'md',
  menuPlacement = 'auto',
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  createOptionKey = null, // Key to trigger create option (e.g., 'create')
  onCreateOption = null, // Callback when creating new option
  createOptionLabel = (inputValue) => `Create "${inputValue}"`,
  ...rest
}) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);

  const sizeClasses = {
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
  };

  // Get current value for display
  const getCurrentValue = useCallback(() => {
    if (isMulti) {
      return Array.isArray(value) ? value.map((v) => v.value || v) : [];
    }
    return value?.value ?? value ?? '';
  }, [isMulti, value]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    let filtered = isSearchable && searchText
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(searchText.toLowerCase())
        )
      : options;

    // Hide selected options if configured
    if (hideSelectedOptions && isMulti) {
      const currentVal = getCurrentValue();
      filtered = filtered.filter((opt) => !currentVal.includes(opt.value));
    }

    return filtered;
  }, [options, searchText, isSearchable, hideSelectedOptions, isMulti, getCurrentValue]);

  // Check if we should show create option
  const showCreateOption = useMemo(() => {
    if (!createOptionKey || !onCreateOption || !searchText.trim()) return false;
    const searchLower = searchText.toLowerCase().trim();
    const exactMatch = options.some(
      (opt) => opt.label.toLowerCase() === searchLower
    );
    return !exactMatch;
  }, [createOptionKey, onCreateOption, searchText, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isSearchable]);

  // Reset focused index when options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredOptions.length]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      if (isOpen) setSearchText('');
    }
  };

  const handleSelectOption = (option) => {
    if (onChange) {
      if (isMulti) {
        const currentVal = getCurrentValue();
        const isSelected = currentVal.includes(option.value);
        let newSelected;
        if (isSelected) {
          newSelected = options.filter(
            (opt) => currentVal.includes(opt.value) && opt.value !== option.value
          );
        } else {
          const currentOptions = options.filter((opt) =>
            currentVal.includes(opt.value)
          );
          newSelected = [...currentOptions, option];
        }
        onChange(newSelected);
      } else {
        onChange(option);
        if (closeMenuOnSelect) {
          setIsOpen(false);
          setSearchText('');
        }
      }
    }
  };

  const handleRemoveItem = (e, val) => {
    e.stopPropagation();
    if (onChange && isMulti) {
      const currentVal = getCurrentValue();
      const newSelected = options.filter(
        (opt) => currentVal.includes(opt.value) && opt.value !== val
      );
      onChange(newSelected);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(isMulti ? [] : null);
    }
  };

  const handleCreateOption = () => {
    if (onCreateOption && searchText.trim()) {
      onCreateOption(searchText.trim());
      setSearchText('');
      if (closeMenuOnSelect && !isMulti) {
        setIsOpen(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    const totalOptions = filteredOptions.length + (showCreateOption ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : totalOptions - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          if (showCreateOption && focusedIndex === filteredOptions.length) {
            handleCreateOption();
          } else if (filteredOptions[focusedIndex]) {
            handleSelectOption(filteredOptions[focusedIndex]);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchText('');
        break;
      default:
        break;
    }
  };

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && menuRef.current) {
      const focusedElement = menuRef.current.querySelector(
        `[data-index="${focusedIndex}"]`
      );
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  const renderSelectedValue = () => {
    const currentVal = getCurrentValue();

    if (isMulti) {
      if (!currentVal || currentVal.length === 0) {
        return <span className={styles.placeholder}>{placeholder}</span>;
      }
      return (
        <div className={styles.chipContainer}>
          {currentVal.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <span key={val} className={styles.chip}>
                {option?.label || val}
                <button
                  type="button"
                  className={styles.chipDelete}
                  onClick={(e) => handleRemoveItem(e, val)}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label={`Remove ${option?.label || val}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      );
    }

    if (!currentVal) {
      return <span className={styles.placeholder}>{placeholder}</span>;
    }

    const option = options.find((opt) => opt.value === currentVal);
    // Prefer the label already carried on the selected value object over the
    // raw id — options may still be loading (async fetch), and falling back
    // to the id causes a visible flicker before the matching option arrives.
    const valueLabel =
      value && typeof value === 'object' ? value.label : undefined;
    return <span>{option?.label ?? valueLabel ?? currentVal}</span>;
  };

  const currentVal = getCurrentValue();
  const hasValue = isMulti ? currentVal.length > 0 : currentVal !== '';

  return (
    <div
      ref={wrapperRef}
      className={`${styles.dropdownWrapper} ${className}`}
      {...rest}
    >
      {label && (
        <label
          htmlFor={name}
          className={`${styles.label} ${labelClassName} ${
            disabled ? styles.labelDisabled : ''
          }`}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div
        className={`${styles.selectContainer} ${sizeClasses[size] || sizeClasses.md} ${
          error ? styles.selectError : ''
        } ${disabled ? styles.selectDisabled : ''} ${isOpen ? styles.selectFocused : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${name}-listbox`}
        aria-labelledby={label ? `${name}-label` : undefined}
        aria-describedby={error || helperText ? `${name}-helper` : undefined}
        onBlur={onBlur}
      >
        <div className={styles.valueContainer}>{renderSelectedValue()}</div>

        <div className={styles.indicators}>
          {isLoading && <span className={styles.loadingSpinner} />}
          {isClearable && hasValue && !disabled && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <span className={`${styles.arrow} ${isOpen ? styles.arrowUp : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={`${styles.menu} ${
            menuPlacement === 'top' ? styles.menuTop : styles.menuBottom
          }`}
          id={`${name}-listbox`}
          role="listbox"
          aria-multiselectable={isMulti}
        >
          {isSearchable && (
            <div className={styles.searchContainer}>
              <span className={styles.searchIcon}>🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className={styles.optionsList}>
            {isLoading ? (
              <div className={styles.optionDisabled}>{loadingMessage()}</div>
            ) : filteredOptions.length === 0 && !showCreateOption ? (
              <div className={styles.optionDisabled}>{noOptionsMessage()}</div>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const isSelected = isMulti
                    ? currentVal.includes(option.value)
                    : currentVal === option.value;
                  return (
                    <div
                      key={option.value}
                      data-index={index}
                      className={`${styles.option} ${
                        isSelected ? styles.optionSelected : ''
                      } ${focusedIndex === index ? styles.optionFocused : ''}`}
                      onClick={() => handleSelectOption(option)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {isMulti && (
                        <span className={styles.checkbox}>
                          {isSelected ? '☑' : '☐'}
                        </span>
                      )}
                      {option.label}
                    </div>
                  );
                })}
                {showCreateOption && (
                  <div
                    data-index={filteredOptions.length}
                    className={`${styles.option} ${styles.createOption} ${
                      focusedIndex === filteredOptions.length
                        ? styles.optionFocused
                        : ''
                    }`}
                    onClick={handleCreateOption}
                    role="option"
                  >
                    <span className={styles.createIcon}>+</span>
                    {createOptionLabel(searchText)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {(error || helperText) && (
        <span
          id={`${name}-helper`}
          className={`${styles.helperText} ${error ? styles.errorText : ''}`}
        >
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default CustomDropdown;
